import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { authClient } from '../../utils/amplifyClient';
import { useFormsContext } from '../../contexts/FormsContext';
import { PlanCategory } from '../../types/trainingTypes';
import {
    createForm as createFormMutation,
    updateForm as updateFormMutation,
} from '../../graphql/mutations';

// Question types available for quizzes
const QUESTION_TYPES = [
    { value: 'yesNo', label: 'Yes / No' },
    { value: 'dropdown', label: 'Multiple Choice' },
    { value: 'checkbox', label: 'Checkbox (Multi-select)' },
];

interface QuizQuestion {
    id: string;
    prompt: string;
    type: string;
    description: string;
    options: string[];
    correctAnswer?: string;
}

interface QuizBuilderModalProps {
    visible: boolean;
    onDismiss: () => void;
    category: PlanCategory;
    companyId: string;
    planId: string;
    onQuizSaved: (categoryId: string, quizFormId: string) => void;
}

export const QuizBuilderModal = ({
    visible,
    onDismiss,
    category,
    companyId,
    onQuizSaved,
}: QuizBuilderModalProps) => {
    const { refreshForms } = useFormsContext();

    const [quizName, setQuizName] = useState('');
    const [questions, setQuestions] = useState<QuizQuestion[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [existingFormId, setExistingFormId] = useState<string | null>(null);

    // Load existing quiz if category already has one
    useEffect(() => {
        if (visible && category.quizFormId) {
            loadExistingQuiz(category.quizFormId);
        } else if (visible) {
            setQuizName(`Quiz: ${category.name}`);
            setQuestions([]);
            setExistingFormId(null);
        }
    }, [visible, category]);

    const loadExistingQuiz = async (formId: string) => {
        setIsLoading(true);
        try {
            const result = await authClient.graphql({
                query: /* GraphQL */ `query GetForm($id: ID!) { getForm(id: $id) { id name formData } }`,
                variables: { id: formId },
            });
            const form = (result as any).data.getForm;
            if (form) {
                setExistingFormId(form.id);
                setQuizName(form.name || `Quiz: ${category.name}`);
                const parsed = JSON.parse(form.formData || '[]');
                const loadedQuestions: QuizQuestion[] = [];
                parsed.forEach((section: any) => {
                    if (section.type === 'section' && section.prompts) {
                        section.prompts.forEach((p: any) => {
                            loadedQuestions.push({
                                id: generateId(),
                                prompt: p.component || '',
                                type: p.type || 'yesNo',
                                description: p.description || '',
                                options: p.options || [],
                                correctAnswer: p.correctAnswer || '',
                            });
                        });
                    } else if (section.type === 'standAloneQuestion') {
                        loadedQuestions.push({
                            id: generateId(),
                            prompt: section.prompt || '',
                            type: 'sectionQuestion',
                            description: section.description || '',
                            options: [],
                            correctAnswer: section.correctAnswer || '',
                        });
                    }
                });
                setQuestions(loadedQuestions);
            }
        } catch (error) {
            console.error('Error loading quiz form:', error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
        }
    };

    const addQuestion = () => {
        setQuestions(prev => [
            ...prev,
            { id: generateId(), prompt: '', type: 'yesNo', description: '', options: [], correctAnswer: '' },
        ]);
    };

    const updateQuestion = (id: string, field: keyof QuizQuestion, value: any) => {
        setQuestions(prev => prev.map(q => (q.id === id ? { ...q, [field]: value } : q)));
    };

    const removeQuestion = (id: string) => {
        setQuestions(prev => prev.filter(q => q.id !== id));
    };

    const moveQuestion = (id: string, direction: 'up' | 'down') => {
        setQuestions(prev => {
            const idx = prev.findIndex(q => q.id === id);
            if (idx < 0) return prev;
            const newIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (newIdx < 0 || newIdx >= prev.length) return prev;
            const updated = [...prev];
            [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
            return updated;
        });
    };

    const handleSave = async () => {
        if (!quizName.trim() || questions.length === 0) return;
        setIsSaving(true);

        try {
            const formData = [{
                type: 'section',
                name: category.name,
                prompts: questions.map(q => ({
                    type: q.type,
                    component: q.prompt,
                    description: q.description,
                    options: q.options,
                    correctAnswer: q.correctAnswer,
                })),
            }];

            const formDataJson = JSON.stringify(formData);
            let savedFormId: string;

            if (existingFormId) {
                await authClient.graphql({
                    query: updateFormMutation,
                    variables: { input: { id: existingFormId, name: quizName.trim(), formData: formDataJson } },
                });
                savedFormId = existingFormId;
            } else {
                const result = await authClient.graphql({
                    query: createFormMutation,
                    variables: {
                        input: {
                            name: quizName.trim(),
                            companyId,
                            formType: 'trainingQuiz',
                            formData: formDataJson,
                            inspectionForm: false,
                            dailyServiceLogForm: false,
                            pmServiceScheduleForm: false,
                        },
                    },
                });
                savedFormId = (result as any).data.createForm.id;
                setExistingFormId(savedFormId);
            }

            await refreshForms();
            onQuizSaved(category.id, savedFormId);
            onDismiss();
        } catch (error) {
            console.error('Error saving quiz:', error);
            Sentry.captureException(error);
        } finally {
            setIsSaving(false);
        }
    };

    const isValid = quizName.trim().length > 0 && questions.length > 0 &&
        questions.every(q => q.prompt.trim().length > 0);

    if (!visible) return null;

    return (
        <div className="dtp-overlay" onClick={onDismiss}>
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: '#fff',
                    borderRadius: 12,
                    width: 700,
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 24px', borderBottom: '1px solid #e2e8f0',
                }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0f172a' }}>
                        {existingFormId ? `Edit Quiz: ${category.name}` : `Create Quiz: ${category.name}`}
                    </h3>
                    <button
                        onClick={onDismiss}
                        style={{
                            background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
                            color: '#64748b', padding: '4px 8px', borderRadius: 4, lineHeight: 1,
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
                    {isLoading ? (
                        <p style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>Loading quiz...</p>
                    ) : (
                        <>
                            {/* Quiz Name */}
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#475569', marginBottom: 6 }}>
                                    Quiz Name
                                </label>
                                <input
                                    type="text"
                                    value={quizName}
                                    onChange={e => setQuizName(e.target.value)}
                                    placeholder={`Quiz: ${category.name}`}
                                    style={{
                                        width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0',
                                        borderRadius: 6, fontSize: 14, boxSizing: 'border-box', color: '#1e293b',
                                    }}
                                />
                            </div>

                            {/* Questions header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                                    Questions ({questions.length})
                                </h4>
                                <button
                                    onClick={addQuestion}
                                    style={{
                                        padding: '8px 14px', borderRadius: 6, border: 'none',
                                        background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 500,
                                        cursor: 'pointer',
                                    }}
                                >
                                    + Add Question
                                </button>
                            </div>

                            {questions.length === 0 && (
                                <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>
                                    No questions yet. Click &ldquo;Add Question&rdquo; to start building the quiz.
                                </p>
                            )}

                            {/* Question cards */}
                            {questions.map((q, idx) => (
                                <div
                                    key={q.id}
                                    style={{
                                        border: '1px solid #e2e8f0', borderRadius: 10, padding: 20,
                                        marginBottom: 16, background: '#f8fafc',
                                    }}
                                >
                                    {/* Question card header */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                                            Question {idx + 1}
                                        </span>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <button
                                                onClick={() => moveQuestion(q.id, 'up')}
                                                disabled={idx === 0}
                                                style={{
                                                    width: 28, height: 28, borderRadius: 4, border: '1px solid #e2e8f0',
                                                    background: '#fff', cursor: idx === 0 ? 'not-allowed' : 'pointer',
                                                    color: idx === 0 ? '#cbd5e1' : '#475569', fontSize: 14,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}
                                                aria-label="Move up"
                                            >
                                                ↑
                                            </button>
                                            <button
                                                onClick={() => moveQuestion(q.id, 'down')}
                                                disabled={idx === questions.length - 1}
                                                style={{
                                                    width: 28, height: 28, borderRadius: 4, border: '1px solid #e2e8f0',
                                                    background: '#fff', cursor: idx === questions.length - 1 ? 'not-allowed' : 'pointer',
                                                    color: idx === questions.length - 1 ? '#cbd5e1' : '#475569', fontSize: 14,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}
                                                aria-label="Move down"
                                            >
                                                ↓
                                            </button>
                                            <button
                                                onClick={() => removeQuestion(q.id)}
                                                style={{
                                                    width: 28, height: 28, borderRadius: 4, border: '1px solid #fecaca',
                                                    background: '#fef2f2', cursor: 'pointer', color: '#dc2626', fontSize: 14,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}
                                                aria-label="Remove question"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>

                                    {/* Question field */}
                                    <div style={{ marginBottom: 12 }}>
                                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 4 }}>
                                            Question
                                        </label>
                                        <input
                                            type="text"
                                            value={q.prompt}
                                            onChange={e => updateQuestion(q.id, 'prompt', e.target.value)}
                                            placeholder="Enter the question..."
                                            style={{
                                                width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0',
                                                borderRadius: 6, fontSize: 14, boxSizing: 'border-box', color: '#1e293b',
                                            }}
                                        />
                                    </div>

                                    {/* Description field */}
                                    <div style={{ marginBottom: 12 }}>
                                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 4 }}>
                                            Description (optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={q.description}
                                            onChange={e => updateQuestion(q.id, 'description', e.target.value)}
                                            placeholder="Additional context for the question"
                                            style={{
                                                width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0',
                                                borderRadius: 6, fontSize: 14, boxSizing: 'border-box', color: '#1e293b',
                                            }}
                                        />
                                    </div>

                                    {/* Question Type */}
                                    <div style={{ marginBottom: 12 }}>
                                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 4 }}>
                                            Question Type
                                        </label>
                                        <select
                                            value={q.type}
                                            onChange={e => updateQuestion(q.id, 'type', e.target.value)}
                                            style={{
                                                width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0',
                                                borderRadius: 6, fontSize: 14, color: '#1e293b', background: '#fff',
                                            }}
                                        >
                                            {QUESTION_TYPES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Options (for dropdown/checkbox types) */}
                                    {(q.type === 'dropdown' || q.type === 'checkbox') && (
                                        <div style={{ marginBottom: 12 }}>
                                            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
                                                Options
                                            </label>
                                            {/* Existing options as removable rows */}
                                            {q.options.length > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                                                    {q.options.map((opt, optIdx) => (
                                                        <div key={optIdx} style={{
                                                            display: 'flex', alignItems: 'center', gap: 8,
                                                            padding: '7px 12px', background: '#fff', border: '1px solid #e2e8f0',
                                                            borderRadius: 6,
                                                        }}>
                                                            <span style={{ flex: 1, fontSize: 13, color: '#1e293b' }}>{opt}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const updated = q.options.filter((_, i) => i !== optIdx);
                                                                    updateQuestion(q.id, 'options', updated);
                                                                    // Also clean up correctAnswer if this option was selected
                                                                    if (q.type === 'dropdown' && (q.correctAnswer || '').toLowerCase().trim() === opt.toLowerCase().trim()) {
                                                                        updateQuestion(q.id, 'correctAnswer', '');
                                                                    }
                                                                    if (q.type === 'checkbox') {
                                                                        const correctArr = (q.correctAnswer || '').split(',').map(s => s.trim()).filter(Boolean);
                                                                        const cleaned = correctArr.filter(v => v.toLowerCase() !== opt.toLowerCase());
                                                                        updateQuestion(q.id, 'correctAnswer', cleaned.join(','));
                                                                    }
                                                                }}
                                                                style={{
                                                                    background: 'none', border: 'none', color: '#dc2626',
                                                                    cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '2px 6px',
                                                                }}
                                                                aria-label={`Remove ${opt}`}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Add new option input */}
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <input
                                                    type="text"
                                                    placeholder="Type an option and press Enter or click Add"
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            const val = (e.target as HTMLInputElement).value.trim();
                                                            if (val && !q.options.includes(val)) {
                                                                updateQuestion(q.id, 'options', [...q.options, val]);
                                                                (e.target as HTMLInputElement).value = '';
                                                            }
                                                        }
                                                    }}
                                                    id={`option-input-${q.id}`}
                                                    style={{
                                                        flex: 1, padding: '9px 12px', border: '1px solid #e2e8f0',
                                                        borderRadius: 6, fontSize: 14, color: '#1e293b',
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const input = document.getElementById(`option-input-${q.id}`) as HTMLInputElement;
                                                        const val = input?.value.trim();
                                                        if (val && !q.options.includes(val)) {
                                                            updateQuestion(q.id, 'options', [...q.options, val]);
                                                            input.value = '';
                                                            input.focus();
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '9px 14px', border: 'none', borderRadius: 6,
                                                        background: '#3b82f6', color: '#fff', fontSize: 13,
                                                        fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    + Add
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Correct Answer */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 4 }}>
                                            Correct Answer
                                        </label>
                                        {q.type === 'yesNo' && (
                                            <>
                                                <span style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
                                                    Select the correct answer
                                                </span>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    {['Yes', 'No'].map(opt => (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            onClick={() => updateQuestion(q.id, 'correctAnswer', opt)}
                                                            style={{
                                                                padding: '8px 20px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                                                                border: (q.correctAnswer || '').toLowerCase() === opt.toLowerCase()
                                                                    ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                                                                background: (q.correctAnswer || '').toLowerCase() === opt.toLowerCase()
                                                                    ? '#eff6ff' : '#fff',
                                                                color: '#1e293b', cursor: 'pointer',
                                                            }}
                                                        >
                                                            {opt}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                        {q.type === 'dropdown' && (
                                            <>
                                                <span style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
                                                    Select the correct option
                                                </span>
                                                {q.options.length === 0 ? (
                                                    <p style={{ fontSize: 12, color: '#94a3b8', margin: 0, fontStyle: 'italic' }}>
                                                        Add options above first
                                                    </p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                        {q.options.map((opt, optIdx) => (
                                                            <label
                                                                key={optIdx}
                                                                style={{
                                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                                    padding: '7px 12px', borderRadius: 6, cursor: 'pointer',
                                                                    border: (q.correctAnswer || '').toLowerCase().trim() === opt.toLowerCase().trim()
                                                                        ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                                                                    background: (q.correctAnswer || '').toLowerCase().trim() === opt.toLowerCase().trim()
                                                                        ? '#eff6ff' : '#fff',
                                                                }}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name={`correct-${q.id}`}
                                                                    checked={(q.correctAnswer || '').toLowerCase().trim() === opt.toLowerCase().trim()}
                                                                    onChange={() => updateQuestion(q.id, 'correctAnswer', opt)}
                                                                    style={{ width: 14, height: 14 }}
                                                                />
                                                                <span style={{ fontSize: 13, color: '#1e293b' }}>{opt}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {q.type === 'checkbox' && (
                                            <>
                                                <span style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
                                                    Check all correct answers
                                                </span>
                                                {q.options.length === 0 ? (
                                                    <p style={{ fontSize: 12, color: '#94a3b8', margin: 0, fontStyle: 'italic' }}>
                                                        Add options above first
                                                    </p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                        {q.options.map((opt, optIdx) => {
                                                            const correctArr = (q.correctAnswer || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                                                            const isCorrect = correctArr.includes(opt.toLowerCase().trim());
                                                            return (
                                                                <label
                                                                    key={optIdx}
                                                                    style={{
                                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                                        padding: '7px 12px', borderRadius: 6, cursor: 'pointer',
                                                                        border: isCorrect ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                                                                        background: isCorrect ? '#eff6ff' : '#fff',
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isCorrect}
                                                                        onChange={e => {
                                                                            const current = (q.correctAnswer || '').split(',').map(s => s.trim()).filter(Boolean);
                                                                            const updated = e.target.checked
                                                                                ? [...current, opt]
                                                                                : current.filter(v => v.toLowerCase().trim() !== opt.toLowerCase().trim());
                                                                            updateQuestion(q.id, 'correctAnswer', updated.join(','));
                                                                        }}
                                                                        style={{ width: 14, height: 14 }}
                                                                    />
                                                                    <span style={{ fontSize: 13, color: '#1e293b' }}>{opt}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex', justifyContent: 'flex-end', gap: 10,
                    padding: '14px 24px', borderTop: '1px solid #e2e8f0',
                }}>
                    <button
                        onClick={onDismiss}
                        style={{
                            padding: '10px 20px', border: '2px solid #d1d5db', borderRadius: 6,
                            background: '#f9fafb', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#374151',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!isValid || isSaving}
                        style={{
                            padding: '10px 20px', border: 'none', borderRadius: 6,
                            background: isValid && !isSaving ? '#3b82f6' : '#94a3b8',
                            color: '#fff', cursor: isValid && !isSaving ? 'pointer' : 'not-allowed',
                            fontSize: 14, fontWeight: 600,
                        }}
                    >
                        {isSaving ? 'Saving...' : existingFormId ? 'Update Quiz' : 'Save Quiz'}
                    </button>
                </div>
            </div>
        </div>
    );
};

function generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `q-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
